import { useRef, useState } from "react";
import { Upload, Image as ImageIcon, X, Loader2, Link2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { getAdminToken } from "../api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Image upload + URL field combo for admin forms.
 * - Click "Carica file" → uploads to /api/admin/upload-image → fills value with returned URL
 * - Or paste/edit URL directly
 * - Shows preview with onError fallback
 *
 * Props:
 *   value: string (current image_url)
 *   onChange: (newUrl: string) => void
 *   testId?: string
 */
export default function ImageUploadField({ value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imgError, setImgError] = useState(false);
  const fileRef = useRef(null);

  // Resolve relative /api/images/... URLs to absolute for preview <img>
  const previewSrc = value
    ? value.startsWith("/") ? `${BACKEND_URL}${value}` : value
    : null;

  async function uploadFile(file) {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    setImgError(false);
    try {
      const xhr = new XMLHttpRequest();
      const fd = new FormData();
      fd.append("file", file);

      const result = await new Promise((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error("Risposta non valida")); }
          } else {
            try {
              const j = JSON.parse(xhr.responseText);
              reject(new Error(j.detail || `HTTP ${xhr.status}`));
            } catch { reject(new Error(`HTTP ${xhr.status}`)); }
          }
        };
        xhr.onerror = () => reject(new Error("Errore di rete"));
        xhr.open("POST", `${BACKEND_URL}/api/admin/upload-image`);
        xhr.setRequestHeader("Authorization", `Bearer ${getAdminToken() || ""}`);
        xhr.send(fd);
      });

      onChange(result.url);
      const sizeMB = (result.size / (1024 * 1024)).toFixed(2);
      toast.success("Immagine caricata", {
        description: `${result.width}×${result.height} · ${sizeMB} MB`,
      });
    } catch (e) {
      toast.error("Upload fallito", { description: e.message });
    } finally {
      setBusy(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function onPick(e) {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  }

  function onDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      {/* Preview / drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="relative bg-white border-2 border-dashed border-border rounded-xl overflow-hidden transition hover:border-brand/40"
        style={{ minHeight: 160 }}
      >
        {previewSrc && !imgError ? (
          <>
            <img
              src={previewSrc}
              alt="Preview"
              onError={() => setImgError(true)}
              className="w-full h-48 object-cover"
            />
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={testId ? `${testId}-clear` : undefined}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition"
              aria-label="Rimuovi immagine"
            >
              <X size={16} />
            </button>
          </>
        ) : imgError ? (
          <div className="h-48 flex flex-col items-center justify-center text-center px-4 bg-red-50">
            <AlertCircle className="text-red-500 mb-2" size={28} />
            <div className="text-sm font-semibold text-red-700">Immagine non caricata</div>
            <div className="text-xs text-red-600 mt-1 break-all">{value}</div>
            <button
              type="button"
              onClick={() => { setImgError(false); onChange(""); }}
              className="mt-3 text-xs font-semibold text-red-600 underline"
            >
              Rimuovi e ricarica
            </button>
          </div>
        ) : (
          <label
            className="h-48 flex flex-col items-center justify-center cursor-pointer text-ink-soft hover:bg-bg/50 transition"
          >
            <ImageIcon size={32} className="mb-2 opacity-60" />
            <div className="text-sm font-semibold text-ink">Carica un'immagine</div>
            <div className="text-xs text-ink-soft mt-0.5">JPG, PNG o WebP · max 15 MB · trascinala qui o clicca</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={onPick}
              data-testid={testId ? `${testId}-input` : undefined}
            />
          </label>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/85 backdrop-blur-sm flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-brand" size={24} />
            <div className="text-sm font-semibold text-ink mt-2">Caricamento {progress}%</div>
            <div className="w-40 h-1.5 bg-border rounded-full overflow-hidden mt-2">
              <div className="h-full bg-brand transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Buttons row */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={testId ? `${testId}-pick` : undefined}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand hover:bg-brand-hover text-white text-xs font-semibold rounded-lg disabled:opacity-60 transition"
        >
          <Upload size={14} /> {value ? "Sostituisci file" : "Carica file"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={onPick}
        />
        <span className="text-[11px] text-ink-soft">oppure incolla URL:</span>
      </div>

      {/* URL input fallback */}
      <div className="relative">
        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" size={14} />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => { setImgError(false); onChange(e.target.value); }}
          placeholder="https://… o /api/images/img_xxxx"
          className="w-full pl-9 pr-3 py-2.5 bg-white border border-border rounded-xl text-xs text-ink focus:outline-none focus:border-brand transition font-mono"
          data-testid={testId ? `${testId}-url` : undefined}
        />
      </div>
    </div>
  );
}
